import { available, BPS_DENOMINATOR, isPositiveU64, isU64, unavailable } from './integer.js';
import {
  type OutcomePoolQuoteContext,
  outcomeSpotPriceD9,
  quoteOutcomeBuyExactIn,
  quoteOutcomeBuyForQuoteBudget,
  quoteOutcomeSellExactIn,
  quoteOutcomeSellForQuoteOutput,
} from './outcome-pool.js';
import { absolutePriceImpactBps, effectiveExecutionPriceD9 } from './spot-price.js';
import type { PoolMathResult } from './types.js';

const MINIMUM_PROTECTION_BPS = 100;
const MAXIMUM_PROTECTION_BPS = 2_000;

export interface ExactTradeOutcome {
  readonly marketPubkey: string;
  readonly outcomeIndex: number;
  readonly mint: string;
  readonly context: OutcomePoolQuoteContext;
}

interface ExactIntentPrice {
  readonly spotPriceD9: bigint;
  readonly effectivePriceD9: bigint;
  readonly priceImpactBps: number;
  readonly suggestedSlippageBps: number;
}

export interface ExactBuyIntentInput {
  readonly outcome: ExactTradeOutcome;
  readonly commitmentD6: bigint;
  readonly maximumWalletDebitD6: bigint;
}

export interface ExactBuyIntentQuote extends ExactIntentPrice {
  readonly commitmentD6: bigint;
  readonly feeExcludedQuoteInputD6: bigint;
  readonly buyFeeD6: bigint;
  readonly prizePoolFeeD6: bigint;
  readonly walletDebitD6: bigint;
  readonly outcomeTokensOutD6: bigint;
}

export type ExactSellIntentInput =
  | {
      readonly outcome: ExactTradeOutcome;
      readonly mode: 'target_gross';
      readonly targetGrossD6: bigint;
      readonly maximumSpendableOutcomeD6: bigint;
    }
  | {
      readonly outcome: ExactTradeOutcome;
      readonly mode: 'sell_all';
      readonly maximumSpendableOutcomeD6: bigint;
    };

export interface ExactSellIntentQuote extends ExactIntentPrice {
  readonly mode: 'target_gross' | 'sell_all';
  readonly tokensInD6: bigint;
  readonly grossQuoteOutputD6: bigint;
  readonly feeExcludedQuoteOutputD6: bigint;
  readonly sellBaseFeeD6: bigint;
  readonly prizePoolFeeD6: bigint;
  readonly sellPenaltyFeeD6: bigint;
  readonly netQuoteOutputD6: bigint;
}

export interface ExactSwapIntentInput {
  readonly source: ExactTradeOutcome;
  readonly destination: ExactTradeOutcome;
  readonly maximumSpendableSourceD6: bigint;
  readonly percentageBps: number;
  readonly slippageBps: number;
}

export interface ExactSwapIntentQuote {
  readonly percentageBps: number;
  readonly slippageBps: number;
  readonly sourceTokensInD6: bigint;
  readonly sellGrossQuoteOutputD6: bigint;
  readonly sellBaseFeeD6: bigint;
  readonly sellPrizePoolFeeD6: bigint;
  readonly sellPenaltyFeeD6: bigint;
  readonly expectedSellNetUsdcOutD6: bigint;
  readonly slippageFlooredBudgetD6: bigint;
  readonly buyAmountInD6: bigint;
  readonly buyFeeD6: bigint;
  readonly buyPrizePoolFeeD6: bigint;
  readonly buyWalletDebitD6: bigint;
  readonly buyGrossQuoteInputD6: bigint;
  readonly estimatedDestinationTokensOutD6: bigint;
}

export function quoteExactBuyIntent(
  input: ExactBuyIntentInput,
): PoolMathResult<ExactBuyIntentQuote> {
  const identityFailure = validateOutcome(input.outcome);
  if (identityFailure !== null) return unavailable(identityFailure);
  if (!isU64(input.maximumWalletDebitD6)) return unavailable('invalid_amount');

  const quote = quoteOutcomeBuyExactIn({
    context: input.outcome.context,
    quoteAmountInD6: input.commitmentD6,
  });
  if (!quote.available) return quote;
  if (quote.value.venue !== 'bonding_curve') return unavailable('market_not_quotable');
  if (quote.value.totalQuoteDebitD6 > input.maximumWalletDebitD6) {
    return unavailable('insufficient_balance');
  }

  const price = exactIntentPrice(
    input.outcome.context,
    quote.value.totalQuoteDebitD6,
    quote.value.outcomeAmountOutD6,
  );
  if (!price.available) return price;

  return available({
    commitmentD6: input.commitmentD6,
    feeExcludedQuoteInputD6: quote.value.quoteToCurveD6,
    buyFeeD6: quote.value.buyFeeD6,
    prizePoolFeeD6: quote.value.prizePoolFeeD6,
    walletDebitD6: quote.value.totalQuoteDebitD6,
    outcomeTokensOutD6: quote.value.outcomeAmountOutD6,
    ...price.value,
  });
}

export function quoteExactSellIntent(
  input: ExactSellIntentInput,
): PoolMathResult<ExactSellIntentQuote> {
  const identityFailure = validateOutcome(input.outcome);
  if (identityFailure !== null) return unavailable(identityFailure);
  if (!isPositiveU64(input.maximumSpendableOutcomeD6)) {
    return unavailable('invalid_amount');
  }

  const quote =
    input.mode === 'sell_all'
      ? quoteOutcomeSellExactIn({
          context: input.outcome.context,
          outcomeAmountInD6: input.maximumSpendableOutcomeD6,
        })
      : quoteOutcomeSellForQuoteOutput({
          context: input.outcome.context,
          targetQuoteOutputD6: input.targetGrossD6,
          target: 'gross',
          maximumOutcomeAmountInD6: input.maximumSpendableOutcomeD6,
        });
  if (!quote.available) return quote;
  if (quote.value.venue !== 'bonding_curve') return unavailable('market_not_quotable');

  const price = exactIntentPrice(
    input.outcome.context,
    quote.value.netQuoteOutputD6,
    quote.value.outcomeAmountInD6,
  );
  if (!price.available) return price;

  return available({
    mode: input.mode,
    tokensInD6: quote.value.outcomeAmountInD6,
    grossQuoteOutputD6: quote.value.grossQuoteOutputD6,
    feeExcludedQuoteOutputD6: quote.value.grossQuoteOutputD6,
    sellBaseFeeD6: quote.value.sellBaseFeeD6,
    prizePoolFeeD6: quote.value.prizePoolFeeD6,
    sellPenaltyFeeD6: quote.value.sellPenaltyFeeD6,
    netQuoteOutputD6: quote.value.netQuoteOutputD6,
    ...price.value,
  });
}

export function quoteExactSwapIntent(
  input: ExactSwapIntentInput,
): PoolMathResult<ExactSwapIntentQuote> {
  const identityFailure = validateSwapIdentity(input);
  if (identityFailure !== null) return unavailable(identityFailure);
  if (
    !isPositiveU64(input.maximumSpendableSourceD6) ||
    !Number.isInteger(input.percentageBps) ||
    input.percentageBps < 1 ||
    input.percentageBps > Number(BPS_DENOMINATOR) ||
    !Number.isInteger(input.slippageBps) ||
    input.slippageBps < MINIMUM_PROTECTION_BPS ||
    input.slippageBps > MAXIMUM_PROTECTION_BPS
  ) {
    return unavailable('invalid_amount');
  }

  const sourceTokensInD6 =
    (input.maximumSpendableSourceD6 * BigInt(input.percentageBps)) / BPS_DENOMINATOR;
  if (!isPositiveU64(sourceTokensInD6)) return unavailable('zero_progress');

  const sell = quoteOutcomeSellExactIn({
    context: input.source.context,
    outcomeAmountInD6: sourceTokensInD6,
  });
  if (!sell.available) return sell;
  if (sell.value.venue !== 'bonding_curve') return unavailable('market_not_quotable');

  const slippageFlooredBudgetD6 =
    (sell.value.netQuoteOutputD6 * (BPS_DENOMINATOR - BigInt(input.slippageBps))) / BPS_DENOMINATOR;
  if (!isPositiveU64(slippageFlooredBudgetD6)) return unavailable('zero_progress');

  const buy = quoteOutcomeBuyForQuoteBudget({
    context: input.destination.context,
    maximumQuoteDebitD6: slippageFlooredBudgetD6,
  });
  if (!buy.available) return buy;
  if (buy.value.venue !== 'bonding_curve') return unavailable('market_not_quotable');

  return available({
    percentageBps: input.percentageBps,
    slippageBps: input.slippageBps,
    sourceTokensInD6,
    sellGrossQuoteOutputD6: sell.value.grossQuoteOutputD6,
    sellBaseFeeD6: sell.value.sellBaseFeeD6,
    sellPrizePoolFeeD6: sell.value.prizePoolFeeD6,
    sellPenaltyFeeD6: sell.value.sellPenaltyFeeD6,
    expectedSellNetUsdcOutD6: sell.value.netQuoteOutputD6,
    slippageFlooredBudgetD6,
    buyAmountInD6: buy.value.quoteToCurveD6,
    buyFeeD6: buy.value.buyFeeD6,
    buyPrizePoolFeeD6: buy.value.prizePoolFeeD6,
    buyWalletDebitD6: buy.value.totalQuoteDebitD6,
    buyGrossQuoteInputD6: buy.value.quoteToCurveD6,
    estimatedDestinationTokensOutD6: buy.value.outcomeAmountOutD6,
  });
}

export function suggestedBondingCurveSlippageBps(priceImpactBps: number): number {
  if (!Number.isFinite(priceImpactBps)) return MAXIMUM_PROTECTION_BPS;
  const doubled = Math.trunc(priceImpactBps) * 2;
  return Math.min(Math.max(doubled, MINIMUM_PROTECTION_BPS), MAXIMUM_PROTECTION_BPS);
}

function exactIntentPrice(
  context: OutcomePoolQuoteContext,
  quoteAmountD6: bigint,
  outcomeAmountD6: bigint,
): PoolMathResult<ExactIntentPrice> {
  const spot = outcomeSpotPriceD9(context);
  if (!spot.available) return spot;
  const effective = effectiveExecutionPriceD9(quoteAmountD6, outcomeAmountD6);
  if (!effective.available) return effective;
  const impact = absolutePriceImpactBps(effective.value, spot.value);
  if (!impact.available) return impact;
  const priceImpactBps = Number(impact.value);
  return available({
    spotPriceD9: spot.value,
    effectivePriceD9: effective.value,
    priceImpactBps,
    suggestedSlippageBps: suggestedBondingCurveSlippageBps(priceImpactBps),
  });
}

function validateOutcome(outcome: ExactTradeOutcome): 'snapshot_outcome_missing' | null {
  if (
    outcome.marketPubkey.length === 0 ||
    outcome.mint.length === 0 ||
    !Number.isInteger(outcome.outcomeIndex) ||
    outcome.outcomeIndex < 0 ||
    outcome.context.venue.outcomeIndex !== outcome.outcomeIndex
  ) {
    return 'snapshot_outcome_missing';
  }
  return null;
}

function validateSwapIdentity(
  input: ExactSwapIntentInput,
):
  | 'snapshot_market_mismatch'
  | 'snapshot_outcome_mint_mismatch'
  | 'snapshot_outcome_missing'
  | null {
  const sourceFailure = validateOutcome(input.source);
  if (sourceFailure !== null) return sourceFailure;
  const destinationFailure = validateOutcome(input.destination);
  if (destinationFailure !== null) return destinationFailure;
  if (input.source.marketPubkey !== input.destination.marketPubkey) {
    return 'snapshot_market_mismatch';
  }
  if (
    input.source.outcomeIndex === input.destination.outcomeIndex ||
    input.source.mint === input.destination.mint
  ) {
    return 'snapshot_outcome_mint_mismatch';
  }
  return null;
}
